import { RequestStatus } from 'features/request/requestSlice';

import * as R from 'ramda';
import { useCallback, useEffect, useState } from 'react';

import type { TConstructionTypesIndexes } from '../../types';
import { defaultCounts } from '../consts';
import type { IUseGetIncludedCountDataProps, IUseGetIncludedCountDataResult } from '../types';

interface IUseIncludedCountByConstructionType<T> {
    workOrderId?: number;
    useGetIncludedCountData?: (props: IUseGetIncludedCountDataProps) => IUseGetIncludedCountDataResult<T>;
}

const getSumReducer = (includedFlagGetMap: string[]) => (a: number, i: any) =>
    a + (R.path(includedFlagGetMap, i) ? 1 : 0);

const mapInitialData = (initialData: any[], constructionTypeIdGetMap: string[], includedFlagGetMap: string[]) => {
    const sumReducer = getSumReducer(includedFlagGetMap);

    return Object.keys(defaultCounts).reduce((acc, key) => {
        acc[key as TConstructionTypesIndexes] = initialData
            .filter((i) => String(R.path(constructionTypeIdGetMap, i)) === key)
            .reduce(sumReducer, 0);

        return acc;
    }, defaultCounts);
};

export const useIncludedCountByConstructionType = <T>(props: IUseIncludedCountByConstructionType<T>) => {
    const { workOrderId, useGetIncludedCountData } = props;

    const [includedCount, setIncludedCount] = useState<Record<TConstructionTypesIndexes, number>>();

    const {
        constructionTypeIdGetMap = ['constructionType', 'id'],
        includedFlagGetMap = ['isIncluded'],
        status,
        makeRequest,
        items,
        reset,
    } = useGetIncludedCountData?.({
        workOrderOrChangeId: workOrderId,
    }) || {};

    useEffect(() => {
        if (status === RequestStatus.SUCCESS && items?.length) {
            if (includedCount) {
                const constructionTypeId = R.path(constructionTypeIdGetMap, items[0]) as number;
                const sumReducer = getSumReducer(includedFlagGetMap);

                setIncludedCount({
                    ...includedCount,
                    [constructionTypeId]: items.reduce(sumReducer, 0),
                });
            } else {
                setIncludedCount(mapInitialData(items, constructionTypeIdGetMap, includedFlagGetMap));
            }

            reset?.();
        }
    }, [constructionTypeIdGetMap, includedCount, includedFlagGetMap, items, reset, status]);

    useEffect(() => {
        makeRequest?.();
    }, [makeRequest, workOrderId]);

    const updateIncludedCount = useCallback(
        (constructionTypeId: number, includeLen: number, isReplace = false) => {
            const key = String(constructionTypeId);

            setIncludedCount(
                (prevIncludedCount) =>
                    ({
                        ...prevIncludedCount,

                        [key]: isReplace
                            ? includeLen
                            : (prevIncludedCount?.[key as TConstructionTypesIndexes] || 0) + includeLen,
                    }) as any,
            );
        },
        [setIncludedCount],
    );

    return { includedCount, refreshIncludedCount: makeRequest, updateIncludedCount };
};
